import { z } from 'zod'

import { apiValueSchema, periodResponseSchema, valueStatusSchema } from './common'

/** GET /plates/summary — internal_api.PlateVisitSummaryResponse (ТЗ п. 6.1, 6.2). Считает распознавания номеров. */
export const vehiclesSummarySchema = z.object({
  total_visits: apiValueSchema,
  unique_plates: apiValueSchema,
})
export type VehiclesSummary = z.infer<typeof vehiclesSummarySchema>

export const topPlateSchema = z.object({
  plate: z.string(),
  visits: z.number(),
})
export type TopPlate = z.infer<typeof topPlateSchema>

/**
 * Пустой топ бывает трёх видов, различать по status, а не по длине data (ТЗ п. 6.4, 8):
 * no_repeat_visits — машины были, повторов нет; no_visits — за период распознаваний нет;
 * no_data — станция ещё ничего не распознавала.
 */
export const TOP_PLATES_STATUSES = ['ok', 'no_repeat_visits', 'no_visits', 'no_data'] as const
export type TopPlatesStatus = (typeof TOP_PLATES_STATUSES)[number]

/** GET /plates/top — internal_api.TopPlatesResponse: до пяти номеров по числу посещений. */
export const topPlatesSchema = z
  .object({ data: z.array(topPlateSchema), status: z.string() })
  .transform(({ data, status }) => ({
    data,
    status: (TOP_PLATES_STATUSES as readonly string[]).includes(status)
      ? (status as TopPlatesStatus)
      : data.length > 0
        ? 'ok'
        : 'no_data',
  }))
export type TopPlates = z.infer<typeof topPlatesSchema>

/**
 * Одно распознавание номера — internal_api.PlateVisitResponse. date/time — в поясе станции.
 * camera_name — имя камеры из настроек бэкенда, null для безымянной (тогда показываем camera_ip).
 * has_frame говорит, отдаст ли /plates/visits/:id/frame кадр; frame_url абсолютный при PUBLIC_BASE_URL.
 */
export const plateVisitSchema = z.object({
  id: z.string(),
  plate: z.string(),
  date: z.string(),
  time: z.string(),
  detected_at: z.string(),
  camera_ip: z.string().optional(),
  camera_name: z.string().nullable().optional(),
  confidence: z.number().optional(),
  has_frame: z.boolean().optional(),
  frame_url: z.string().optional(),
})
export type PlateVisit = z.infer<typeof plateVisitSchema>

/** not_found — поиск ничего не нашёл; no_visits и no_data — как у топа (ТЗ п. 6.5). */
export const PLATE_VISITS_STATUSES = ['ok', 'not_found', 'no_visits', 'no_data'] as const
export type PlateVisitsStatus = (typeof PLATE_VISITS_STATUSES)[number]

/** GET /plates/visits — internal_api.PlateVisitListResponse. total — размер всего отфильтрованного журнала. */
export const plateVisitsPageSchema = z
  .object({
    data: z.array(plateVisitSchema),
    status: z.string(),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
  })
  .transform((page) => ({
    ...page,
    status: (PLATE_VISITS_STATUSES as readonly string[]).includes(page.status)
      ? (page.status as PlateVisitsStatus)
      : page.data.length > 0
        ? 'ok'
        : 'no_data',
  }))
export type PlateVisitsPage = z.infer<typeof plateVisitsPageSchema>

/** Бакет графика — internal_api.PlateVisitBucketResponse; visits есть только при ok. */
export const plateVisitBucketSchema = z.object({
  interval_start: z.string(),
  status: valueStatusSchema,
  visits: z.number().nullable().optional(),
})
export type PlateVisitBucket = z.infer<typeof plateVisitBucketSchema>

/**
 * GET /plates/chart?period|from&to — internal_api.PlateVisitsChartResponse (ТЗ п. 6.3): посещения
 * по плотным бакетам с шагом period.bucket_size. Столбик считает посещения со склейкой распознаваний
 * одного номера в пятиминутном окне, как /plates/visits, поэтому total совпадает с журналом и может быть
 * ниже «Всего посещений». Пустой бакет при живом ANPR — ok с нулём, при простое дольше суток — no_data.
 */
export const plateVisitsChartSchema = z.object({
  period: periodResponseSchema,
  buckets: z.array(plateVisitBucketSchema),
  total: apiValueSchema,
})
export type PlateVisitsChart = z.infer<typeof plateVisitsChartSchema>
